import {
  createContext,
  cloneElement,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  isValidElement,
  type ReactNode,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { isTextEntryTarget } from "@/lib/editable-target";
import {
  clampPopupToViewport,
  menuAtAnchorRect,
  type PopupAlign,
  type PopupAnchorRect,
} from "@/lib/popup-position";
import { usePopupPresence } from "@/hooks/use-popup-presence";
import { ContextMenuPositionContext, sameSpot } from "./context-menu-position";
import {
  MenuItemContent,
  menuItemVariants,
  menuLabelClassName,
  menuSurfaceVariants,
} from "./dropdown-menu";
import {
  MenuSurfaceContext,
  menuSurfaceProps,
  useMenuSurface,
  type MenuSurface,
} from "./menu-surface";

/**
 * ContextMenu — lightweight right-click context menu.
 *
 * Built without @radix-ui/react-context-menu (not installed).
 * Matches the visual style of DropdownMenu: dark glass background,
 * rounded corners, subtle border, small text.
 *
 * Usage:
 *   <ContextMenu>
 *     <ContextMenuTrigger>
 *       <div>Right-click me</div>
 *     </ContextMenuTrigger>
 *     <ContextMenuContent>
 *       <ContextMenuItem onClick={handleRename}>Rename</ContextMenuItem>
 *       <ContextMenuSeparator />
 *       <ContextMenuItem destructive onClick={handleDelete}>Delete</ContextMenuItem>
 *     </ContextMenuContent>
 *   </ContextMenu>
 */

/* ---------- Context ---------- */

interface ContextMenuState {
  open: boolean;
  anchor: ContextMenuAnchor;
  openAt: (x: number, y: number) => void;
  close: () => void;
}

const ContextMenuContext = createContext<ContextMenuState | null>(null);

function useContextMenuContext() {
  const ctx = useContext(ContextMenuContext);
  if (!ctx) {
    throw new Error(
      "ContextMenu sub-components must be used inside <ContextMenu>",
    );
  }
  return ctx;
}

/**
 * Close the nearest ContextMenu. No-op outside a ContextMenu so submenu
 * components stay usable in isolation (tests, future popovers).
 */
export function useContextMenuClose(): () => void {
  const ctx = useContext(ContextMenuContext);
  return ctx?.close ?? noopClose;
}

function noopClose() {}

/* ---------- ContextMenu (root) ---------- */

/**
 * Where a controlled menu opens, in viewport coordinates: at a point (a
 * right-click), or from a button's box, lined up with its start or end edge
 * and flipping above it without room below.
 */
export type ContextMenuAnchor =
  | { x: number; y: number }
  | { rect: PopupAnchorRect; align: PopupAlign };

interface ContextMenuProps {
  children: ReactNode;
  /**
   * Controlled: open at this point, closed on `null` (the menu keeps the last
   * point while it fades out). Omitted: a `ContextMenuTrigger` opens it.
   */
  anchor?: ContextMenuAnchor | null;
  /** Controlled: the menu asks to close. */
  onClose?: () => void;
}

export function ContextMenu({ children, anchor, onClose }: ContextMenuProps) {
  const [open, setOpen] = useState(false);
  const [shownAnchor, setShownAnchor] = useState<ContextMenuAnchor>({
    x: 0,
    y: 0,
  });
  const controlled = anchor !== undefined;
  if (controlled && anchor && anchor !== shownAnchor) setShownAnchor(anchor);

  function openAt(x: number, y: number) {
    setShownAnchor({ x, y });
    setOpen(true);
  }

  function close() {
    if (controlled) onClose?.();
    else setOpen(false);
  }

  return (
    <ContextMenuContext.Provider
      value={{
        open: controlled ? anchor !== null : open,
        anchor: shownAnchor,
        openAt,
        close,
      }}
    >
      {children}
    </ContextMenuContext.Provider>
  );
}

/* ---------- ContextMenuTrigger ---------- */

interface ContextMenuTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}

export function ContextMenuTrigger({
  children,
  asChild,
}: ContextMenuTriggerProps) {
  const { openAt } = useContextMenuContext();

  function handleContextMenu(e: React.MouseEvent) {
    // A text field keeps the system text menu (Cut / Copy / Paste).
    if (isTextEntryTarget(e.target)) return;
    e.preventDefault();
    openAt(e.clientX, e.clientY);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key !== "ContextMenu" && !(e.shiftKey && e.key === "F10")) return;
    if (isTextEntryTarget(e.target)) return;
    e.preventDefault();
    const target = e.target instanceof HTMLElement ? e.target : e.currentTarget;
    const rect = target.getBoundingClientRect();
    openAt(rect.left + 8, rect.bottom);
  }

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{
      onContextMenu?: (e: React.MouseEvent) => void;
      onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
    }>;
    return cloneElement(child, {
      onContextMenu: (e: React.MouseEvent) => {
        child.props.onContextMenu?.(e);
        if (!e.defaultPrevented) handleContextMenu(e);
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
        child.props.onKeyDown?.(e);
        if (!e.defaultPrevented) handleKeyDown(e);
      },
    });
  }

  return (
    <div
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      className="contents"
    >
      {children}
    </div>
  );
}

/* ---------- ContextMenuContent ---------- */

interface ContextMenuContentProps {
  children: ReactNode;
  className?: string;
  /** Its material; by default the one its surface declares. */
  surface?: MenuSurface;
}

export function ContextMenuContent({
  children,
  className,
  surface,
}: ContextMenuContentProps) {
  const { open, anchor, close } = useContextMenuContext();
  const material = useMenuSurface(surface);
  const paint = menuSurfaceProps(material, {
    nativeLevel: "root",
    nativeGroup: "menus",
  });
  const presence = usePopupPresence(open);
  const menuRef = useRef<HTMLDivElement>(null);
  // Measured before paint and again whenever the menu grows (rows that
  // arrive with an item's detail): a menu near a viewport edge (the
  // bottom-right time-page map, an opened item's ⋯ by the Dock) stays fully
  // visible. A closing menu keeps its place while it fades out.
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return;
    const place = () => {
      const { width, height } = menu.getBoundingClientRect();
      const next =
        "rect" in anchor
          ? menuAtAnchorRect(anchor.rect, width, height, anchor.align)
          : clampPopupToViewport(anchor.x, anchor.y, width, height);
      setPosition((current) => (sameSpot(current, next) ? current : next));
    };
    place();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(place);
    observer.observe(menu);
    return () => observer.disconnect();
  }, [open, anchor]);

  useEffect(() => {
    if (!open || !presence.mounted) return;
    menuRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')
      ?.focus();
  }, [open, presence.mounted]);

  // Close on outside interaction. Capture phase so a stopPropagation()
  // anywhere in the tree (canvas, overlays) can't keep the menu open.
  // Window blur covers desktop click-through mode, where outside clicks
  // go to the OS and never reach this document.
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    }

    function handleWindowBlur() {
      close();
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  if (!presence.mounted) return null;

  function handleMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (
      e.key !== "ArrowDown" &&
      e.key !== "ArrowUp" &&
      e.key !== "Home" &&
      e.key !== "End"
    ) {
      return;
    }
    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement
    ) {
      return;
    }
    const menu = menuRef.current;
    if (!menu) return;
    const items = Array.from(
      menu.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)'),
    ).filter((item) => item.closest('[role="menu"]') === menu);
    if (items.length === 0) return;

    e.preventDefault();
    const currentIndex = items.findIndex((item) => item === activeElement);
    let nextIndex: number;
    if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = items.length - 1;
    else if (e.key === "ArrowDown") {
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
    } else {
      nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    }
    items[nextIndex]?.focus();
  }

  return createPortal(
    <div
      ref={menuRef}
      className={cn(
        paint.className,
        menuSurfaceVariants({ surface: material }),
        "fixed min-w-[160px]",
        className,
      )}
      style={{ position: "fixed", left: position.x, top: position.y }}
      data-interactive="true"
      data-testid="context-menu-content"
      data-state={presence.state}
      role="menu"
      onKeyDown={handleMenuKeyDown}
      {...paint.attributes}
    >
      <MenuSurfaceContext.Provider value={material}>
        <ContextMenuPositionContext.Provider value={position}>
          {children}
        </ContextMenuPositionContext.Provider>
      </MenuSurfaceContext.Provider>
    </div>,
    document.body,
  );
}

/* ---------- ContextMenuItem ---------- */

interface ContextMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  icon?: ReactNode;
  trailing?: ReactNode;
  "data-testid"?: string;
}

export function ContextMenuItem({
  children,
  onClick,
  disabled = false,
  destructive = false,
  icon,
  trailing,
  "data-testid": testId,
}: ContextMenuItemProps) {
  const { close } = useContextMenuContext();

  function handleClick() {
    close();
    onClick?.();
  }

  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        menuItemVariants({ density: "roomy" }),
        "w-full text-left disabled:pointer-events-none disabled:opacity-40",
        "hover:bg-[var(--color-interactive-hover)] focus:outline-none focus-visible:bg-[var(--color-interactive-hover)]",
        destructive ? "text-danger-light" : "text-primary",
      )}
      onClick={handleClick}
      disabled={disabled}
      data-testid={testId}
    >
      <MenuItemContent icon={icon} trailing={trailing}>
        {children}
      </MenuItemContent>
    </button>
  );
}

/* ---------- ContextMenuLabel ---------- */

/** A non-interactive group header inside a menu or a flyout. */
export function ContextMenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className={menuLabelClassName} data-testid="context-menu-label">
      {children}
    </div>
  );
}

/* ---------- ContextMenuSeparator ---------- */

export function ContextMenuSeparator() {
  return (
    <div
      className="my-appkit-2xs h-px bg-glass-stroke-subtle"
      role="separator"
      data-testid="context-menu-separator"
    />
  );
}
